import { useEffect, useMemo, useState, MouseEvent } from "react";

import { clsx } from "clsx";
import { getBezierPath, useStore } from "reactflow";

import { RVIcon, RVIconSizeOptions, RVIconVariantOptions } from "@regal-voice/ui";

import styles from "./FlowEdges.module.scss";

const foreignObjectWidth = 40;
const foreignObjectHeight = 40;

export interface EdgeProps {
    id: string;
    sourceX: any;
    sourceY: any;
    targetX: any;
    targetY: any;
    sourcePosition: any;
    targetPosition: any;
    style?: any;
    markerEnd?: any;
    connectingNodesFrom: boolean;
    disableDelete?: boolean;
    onRemoveEdge?: (id: string) => void;
    selected?: boolean;
    sourceHandle?: string | null;
}

export function ButtonEdge({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    connectingNodesFrom,
    onRemoveEdge,
    disableDelete = false,
    selected = false,
    sourceHandle: sourceHandleProp,
}: EdgeProps): JSX.Element {
    const [isHovered, setIsHovered] = useState(false);
    const edges = useStore((store) => store.edges);

    const sourceHandle = useMemo(() => {
        if (sourceHandleProp) {
            return sourceHandleProp;
        }
        const edge = Array.from(edges.values()).find((e) => e.id === id);
        return edge?.sourceHandle || null;
    }, [sourceHandleProp, edges, id]);

    const [edgePath, edgeCenterX, edgeCenterY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    const handleMouseEnter = () => {
        setIsHovered(true);
        if (sourceHandle) {
            const handleIdString = String(sourceHandle);
            const handleElement = document.querySelector(`[data-handle-id="${handleIdString}"]`);
            if (handleElement) {
                handleElement.setAttribute("data-edge-hovered", "true");
            }
        }
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        if (sourceHandle) {
            const handleIdString = String(sourceHandle);
            const handleElement = document.querySelector(`[data-handle-id="${handleIdString}"]`);
            if (handleElement) {
                // Only remove if not selected (check if element has selectedEdgeSource class by checking all classes)
                const hasSelectedClass = Array.from(handleElement.classList).some((cls) =>
                    cls.includes("selectedEdgeSource")
                );
                if (!hasSelectedClass) {
                    handleElement.removeAttribute("data-edge-hovered");
                }
            }
        }
    };

    useEffect(() => {
        if (!sourceHandle) {
            return;
        }

        const handleIdString = String(sourceHandle);
        const handleElement = document.querySelector(`[data-handle-id="${handleIdString}"]`);
        if (selected) {
            if (handleElement) {
                handleElement.setAttribute("data-edge-selected", "true");
            }
        } else if (!isHovered) {
            if (handleElement) {
                const edgesFromSameHandle = Array.from(edges.values()).filter(
                    (edge) => edge.sourceHandle === sourceHandle && edge.id !== id
                );
                const hasOtherSelectedEdge = edgesFromSameHandle.some((edge) => edge.selected);
                if (!hasOtherSelectedEdge) {
                    handleElement.removeAttribute("data-edge-selected");
                }
            }
        }
    }, [selected, sourceHandle, isHovered, edges, id]);

    useEffect(() => {
        if (!sourceHandle) {
            return;
        }
        const handleIdString = String(sourceHandle);
        return () => {
            const cleanupHandleElement = document.querySelector(`[data-handle-id="${handleIdString}"]`);
            if (cleanupHandleElement) {
                // Check if any other edges from the same handle are still selected or hovered
                const edgesFromSameHandle = Array.from(edges.values()).filter(
                    (edge) => edge.sourceHandle === sourceHandle && edge.id !== id
                );
                const hasOtherSelectedEdge = edgesFromSameHandle.some((edge) => edge.selected);
                if (!hasOtherSelectedEdge) {
                    cleanupHandleElement.removeAttribute("data-edge-selected");
                    cleanupHandleElement.removeAttribute("data-edge-hovered");
                }
            }
        };
    }, [sourceHandle, edges, id]);

    return (
        <>
            <path
                id={id}
                data-id={id}
                data-source-handle={sourceHandle}
                style={style}
                className={clsx("react-flow__edge-path", {
                    "react-flow__edge-path-selected": selected || isHovered,
                })}
                d={edgePath}
                markerEnd={markerEnd}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                data-selected={selected ? "true" : undefined}
            />

            {!disableDelete && (
                <foreignObject
                    width={foreignObjectWidth}
                    height={foreignObjectHeight}
                    x={edgeCenterX - foreignObjectWidth / 2}
                    y={edgeCenterY - foreignObjectHeight / 2}
                    requiredExtensions="http://www.w3.org/1999/xhtml"
                >
                    <div
                        className={clsx(styles.container, "flex-row justify-center align-center")}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <RVIcon
                            className={clsx(styles.edgeButton, "flow-edge-button", {
                                [styles.connectingEdge]: connectingNodesFrom,
                            })}
                            icon={"close"}
                            variant={RVIconVariantOptions.CIRCLE}
                            size={RVIconSizeOptions.SMALL}
                            onClick={(e: MouseEvent) => {
                                e.stopPropagation();
                                onRemoveEdge?.(id);
                            }}
                        />
                    </div>
                </foreignObject>
            )}
        </>
    );
}
